import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import Sidebar from "../../components/Sidebar";
import Navbar from "../../components/Navbar";
import axiosInstance from "../../../axiosInstance";
import { getJobPortalAuthConfig } from "../../../helpers/jobPortalAuth";
import { loadRazorpayScript } from "../../../helpers/razorpay";
import { downloadSubscriptionInvoice } from "../../../helpers/subscriptionInvoice";

const PortalPlansPage = () => {
  const [loading, setLoading] = useState(true);
  const [buyingId, setBuyingId] = useState("");
  const [plans, setPlans] = useState([]);
  const [audience, setAudience] = useState("");
  const [subscription, setSubscription] = useState(null);
  const [razorpayKey, setRazorpayKey] = useState("");
  const [orders, setOrders] = useState([]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const authConfig = await getJobPortalAuthConfig();
      const [plansResponse, ordersResponse] = await Promise.all([
        axiosInstance.get("/job-portal/plans/me", authConfig),
        axiosInstance.get("/job-portal/subscription/orders", authConfig),
      ]);
      setPlans(plansResponse.data?.data?.plans || []);
      setAudience(plansResponse.data?.data?.audience || "");
      setSubscription(plansResponse.data?.data?.subscription || null);
      setRazorpayKey(plansResponse.data?.data?.razorpayKey || "");
      setOrders(ordersResponse.data?.data?.orders || []);
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to load plans");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const headerTitle = useMemo(() => {
    if (audience === "recruiter") return "Recruiter Plans";
    if (audience === "jobseeker") return "Job Seeker Plans";
    return "Plans";
  }, [audience]);

  const handleBuyPlan = async (plan) => {
    try {
      setBuyingId(plan.id);
      const authConfig = await getJobPortalAuthConfig();
      const checkoutResponse = await axiosInstance.post(
        "/job-portal/subscription/checkout",
        { planId: plan.id },
        authConfig
      );

      const checkoutData = checkoutResponse.data?.data || {};
      const key = checkoutData.razorpayKey || razorpayKey;

      if (!key) {
        toast.error("Razorpay key is missing on the server");
        return;
      }

      const loaded = await loadRazorpayScript();
      if (!loaded) {
        toast.error("Unable to load Razorpay checkout");
        return;
      }

      const options = {
        key,
        amount: checkoutData.amount,
        currency: checkoutData.currency || "INR",
        name: "thejobship ",
        description: `${checkoutData.plan?.title || "Plan"} purchase`,
        order_id: checkoutData.orderId,
        prefill: {
          name: checkoutData.user?.name || "",
          email: checkoutData.user?.email || "",
          contact: checkoutData.user?.phone || "",
        },
        notes: {
          planId: plan.id,
          purchaseId: checkoutData.purchaseId,
          audience: checkoutData.plan?.audience || audience,
        },
        theme: {
          color: "#212529",
        },
        handler: async (paymentResult) => {
          try {
            const verifyResponse = await axiosInstance.post(
              "/job-portal/subscription/verify",
              {
                purchaseId: checkoutData.purchaseId,
                ...paymentResult,
              },
              authConfig
            );

            setSubscription(verifyResponse.data?.data?.subscription || null);
            toast.success("Plan activated successfully");
            fetchPlans();
          } catch (error) {
            toast.error(
              error.response?.data?.message || "Unable to verify payment"
            );
          } finally {
            setBuyingId("");
          }
        },
        modal: {
          ondismiss: () => setBuyingId(""),
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on("payment.failed", (response) => {
        toast.error(
          response?.error?.description || "Payment failed. Please try again."
        );
        setBuyingId("");
      });
      razorpay.open();
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to start checkout");
      setBuyingId("");
    }
  };

  return (
    <>
      <Sidebar />
      <main className="content">
        <Navbar />
        <div className="py-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
            <div>
              <h1 className="h4 mb-1">{headerTitle}</h1>
              <p className="text-muted mb-0">
                Buy and manage your active plan to continue portal actions.
              </p>
            </div>
          </div>

          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div>
                  <div className="small text-uppercase text-muted fw-semibold">
                    Current Subscription
                  </div>
                  <div className="h5 mb-1">
                    {subscription?.planTitle || "No active plan"}
                  </div>
                  <div className="text-muted small">
                    {subscription?.statusLabel || "Inactive"}
                  </div>
                </div>
                <span
                  className={`badge ${
                    subscription?.status === "active"
                      ? "bg-success"
                      : subscription?.status === "pending"
                      ? "bg-warning text-dark"
                      : "bg-secondary"
                  }`}
                >
                  {subscription?.status || "inactive"}
                </span>
              </div>
            </div>
          </div>

          <div className="row g-4">
            {loading ? (
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-muted">Loading plans...</div>
                </div>
              </div>
            ) : plans.length === 0 ? (
              <div className="col-12">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-muted">No plans available right now.</div>
                </div>
              </div>
            ) : (
              plans.map((plan) => (
                <div className="col-12 col-lg-6 col-xl-4" key={plan.id}>
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-body d-flex flex-column">
                      <div className="d-flex justify-content-between align-items-start gap-2 mb-3">
                        <div>
                          <div className="display-6 fw-bold mb-1">₹{plan.price}</div>
                          <h5 className="mb-1">
                            {plan.title}{" "}
                            {plan.isBestValue ? (
                              <span className="text-warning">★</span>
                            ) : null}
                          </h5>
                          <div className="text-muted small">
                            {plan.durationLabel || "Plan"}
                          </div>
                        </div>
                        {plan.badgeText ? (
                          <span className="badge bg-warning text-dark">
                            {plan.badgeText}
                          </span>
                        ) : null}
                      </div>

                      <p className="text-muted small">
                        {plan.description || "No description added yet."}
                      </p>

                      <div className="mt-2 mb-4">
                        {(plan.features || []).length ? (
                          plan.features.map((feature) => (
                            <div className="small mb-2" key={feature}>
                              <i className="bi bi-check2 text-success me-2" />
                              {feature}
                            </div>
                          ))
                        ) : (
                          <div className="small text-muted">No features added.</div>
                        )}
                      </div>

                      <button
                        type="button"
                        className="btn btn-dark mt-auto text-white"
                        disabled={buyingId === plan.id}
                        onClick={() => handleBuyPlan(plan)}
                      >
                        {buyingId === plan.id ? "Processing..." : "Buy Plan"}
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="card border-0 shadow-sm mt-4">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h5 className="mb-1">Subscription Orders</h5>
                  <div className="small text-muted">
                    View your plan purchases and download invoices.
                  </div>
                </div>
              </div>

              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Plan</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Date</th>
                      <th>Invoice</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.length ? (
                      orders.map((order) => (
                        <tr key={order.id}>
                          <td>
                            <div className="fw-semibold">
                              {order.plan?.title || "-"}
                            </div>
                            <div className="small text-muted">
                              {order.plan?.durationLabel || "-"}
                            </div>
                          </td>
                          <td>₹{order.amount || 0}</td>
                          <td>
                            <span
                              className={`badge ${
                                order.status === "paid"
                                  ? "bg-success"
                                  : order.status === "pending"
                                  ? "bg-warning text-dark"
                                  : "bg-danger"
                              }`}
                            >
                              {order.status}
                            </span>
                          </td>
                          <td>
                            {order.purchasedAt
                              ? new Date(order.purchasedAt).toLocaleDateString("en-IN")
                              : new Date(order.createdAt).toLocaleDateString("en-IN")}
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-dark"
                              onClick={() => downloadSubscriptionInvoice(order)}
                            >
                              Download
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center py-4 text-muted">
                          No subscription orders yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default PortalPlansPage;
